
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import TitleSection from "@/components/homepage/TitleSection";
import SwipeSlideSection from "@/components/homepage/SwipeSlideSection";
import FeaturedProcesses from "@/components/homepage/FeaturedProcesses";
import FrameworkCard from "@/components/FrameworkCard";
import { useTranslation } from "react-i18next";

const Index = () => {
  const { t, i18n } = useTranslation('common');

  // Preload framework images for better performance
  useEffect(() => {
    const preloadImages = [
      '/images/frameworks/aifmd.svg',
      '/images/frameworks/csrd.svg',
      '/images/frameworks/nis2.svg',
      '/images/frameworks/custom-ai.svg'
    ];

    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <div className="relative min-h-screen">
      <Helmet>
        <title>{t('index.pageTitle')}</title>
        <meta name="description" content={t('index.description')} />
        {/* Add preload hints for critical assets */}
        <link rel="preload" href="/images/frameworks/aifmd.svg" as="image" />
        <link rel="preload" href="/images/frameworks/csrd.svg" as="image" />
        <link rel="preload" href="/images/frameworks/nis2.svg" as="image" />
        <link rel="preload" href="/images/frameworks/custom-ai.svg" as="image" />
      </Helmet>
      
      {/* Background Elements */}

      {/* Navbar */}
      <header className="sticky top-0 z-50">
        <NavBar />
      </header>
      
      <main>


        <section className="
        h-[70vh] pt-[40px] sm:pt-[50px] md:pt-[70px] lg:pt-[70px] xl:pt-[80px] 2xl:pt-[90px] flex items-center justify-center bg-white backdrop-blur-sm relative overflow-hidden">
          <div className="w-full relative z-10 ">

            <TitleSection />


          </div>
        </section>

        {/* Hero section with adjusted height and enhanced background */}
        <section className="
        h-[70vh] pt-[40px] sm:pt-[50px] md:pt-[70px] lg:pt-[70px] xl:pt-[80px] 2xl:pt-[90px] flex items-center justify-center bg-white backdrop-blur-sm relative overflow-hidden">
          <div className="w-full relative z-10 ">

        <SwipeSlideSection />

          </div>
        </section>
        

        {/* Compliance Frameworks Section */}
        <section className="py-24 bg-gradient-to-b from-white to-gray-200">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-semibold text-center mb-4">
              {t('homepage.frameworks.sectionTitle')}
            </h2>
            <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
              {t('homepage.frameworks.sectionDescription')}
            </p>
            
            {/* Apple-style framework cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 px-4 auto-rows-fr">
              {/* AIFMD Card */}
              <FrameworkCard
                title={t('homepage.frameworks.aifmd.title')}
                description={t('homepage.frameworks.aifmd.description')}
                features={t('homepage.frameworks.aifmd.features', { returnObjects: true })}
                imageSrc="/images/frameworks/aifmd.svg"
                videoSrc="/images/frameworks/aifmd.mp4"
                linkTo="/aifmd"
              />

              {/* CSRD Card */}
              <FrameworkCard
                title={t('homepage.frameworks.csrd.title')}
                description={t('homepage.frameworks.csrd.description')}
                features={t('homepage.frameworks.csrd.features', { returnObjects: true })}
                imageSrc="/images/frameworks/csrd.svg"
                videoSrc="/images/frameworks/csrd.mp4"
                linkTo="/csrd"
              />

              {/* NIS2 Card */}
              <FrameworkCard
                title={t('homepage.frameworks.nis2.title')}
                description={t('homepage.frameworks.nis2.description')}
                features={t('homepage.frameworks.nis2.features', { returnObjects: true })}
                imageSrc="/images/frameworks/nis2.svg"
                videoSrc="/images/frameworks/nis2.mp4"
                linkTo="/nis2"
              />

              {/* Custom AI Workflows Card - With special styling */}
              <FrameworkCard
                title={t('homepage.frameworks.customAI.title')}
                description={t('homepage.frameworks.customAI.description')}
                features={t('homepage.frameworks.customAI.features', { returnObjects: true })}
                imageSrc="/images/frameworks/custom-ai.svg"
                videoSrc="/images/frameworks/custom-ai.mp4"
                linkTo="/platform"
                variant="special"
              />
            </div>
          </div>
        </section>

        {/* Supported Languages Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-semibold text-center mb-4">
              {t('homepage.languages.sectionTitle')}
            </h2>
            <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
              {t('homepage.languages.sectionDescription')}
            </p>
            
            <div className="flex flex-wrap justify-center items-center gap-8 max-w-4xl mx-auto">
              <div className="flex flex-col items-center">
                <div className="h-8 w-12 mb-2 overflow-hidden shadow-sm rounded">
                  <img src="/images/languages/gb.svg" alt="English" className="h-full w-full object-cover" />
                </div>
                <span className="text-sm font-medium">English</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="h-8 w-12 mb-2 overflow-hidden shadow-sm rounded">
                  <img src="/images/languages/de.svg" alt="German" className="h-full w-full object-cover" />
                </div>
                <span className="text-sm font-medium">German</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="h-8 w-12 mb-2 overflow-hidden shadow-sm rounded">
                  <img src="/images/languages/fr.svg" alt="French" className="h-full w-full object-cover" />
                </div>
                <span className="text-sm font-medium">French</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="h-8 w-12 mb-2 overflow-hidden shadow-sm rounded">
                  <img src="/images/languages/es-ct.svg" alt="Spanish" className="h-full w-full object-cover" />
                </div>
                <span className="text-sm font-medium">Spanish</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="h-8 w-12 mb-2 overflow-hidden shadow-sm rounded">
                  <img src="/images/languages/it.svg" alt="Italian" className="h-full w-full object-cover" />
                </div>
                <span className="text-sm font-medium">Italian</span>
              </div>

              <div className="flex flex-col items-center">
                <div className="h-8 w-12 mb-2 overflow-hidden shadow-sm rounded">
                  <img src="/images/languages/us.svg" alt="English (US)" className="h-full w-full object-cover" />
                </div>
                <span className="text-sm font-medium">English (US)</span>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-gray-500">
                {t('homepage.languages.additionalInfo')}
              </p>
            </div>
          </div>
        </section>
        
        {/* Security and Privacy Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                {/* Left side: Text content */}
                <div>
                  <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                    {t('homepage.security.title')}
                    <span className="block text-2xl md:text-3xl text-emerald-700 mt-1">
                      {t('homepage.security.subtitle')}
                    </span>
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="h-6 w-6 bg-emerald-200 rounded-full flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-medium">
                          {t('homepage.security.encryption.title')}
                        </h3>
                        <p className="mt-1 text-gray-600">
                          {t('homepage.security.encryption.description')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="h-6 w-6 bg-emerald-200 rounded-full flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-medium">
                          {t('homepage.security.infrastructure.title')}
                        </h3>
                        <p className="mt-1 text-gray-600">
                          {t('homepage.security.infrastructure.description')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="h-6 w-6 bg-emerald-200 rounded-full flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-medium">
                          {t('homepage.security.access.title')}
                        </h3>
                        <p className="mt-1 text-gray-600">
                          {t('homepage.security.access.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <a 
                      href="/platform"
                      className="inline-flex items-center text-emerald-700 font-medium hover:text-emerald-600 transition-colors"
                    >
                      {t('homepage.security.learnMore')}
                      <svg xmlns="http://www.w3.org/2000/svg" className="ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                  </div>
                </div>
                
                {/* Right side: Security graphic */}
                <div className="relative h-[400px] flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-64 bg-emerald-100 rounded-full opacity-70"></div>
                  </div>
                  
                  <div className="relative z-10 bg-white p-6 rounded-xl shadow-xl border border-gray-100">
                    <div className="w-64 h-64 flex flex-col items-center justify-center text-center p-4">
                      <div className="w-20 h-20 bg-emerald-200 rounded-full flex items-center justify-center mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold">Security Focus</h3>
                      <p className="text-gray-600 mt-2">ISO 27001 certification</p>
                      <div className="mt-4 pt-4 border-t border-gray-100 w-full">
                        <div className="flex justify-center mb-3">
                          <img src="/logos/amazon.png" alt="AWS" className="h-10" />
                        </div>
                        <div className="flex justify-between">
                          <div className="text-left">
                            <div className="text-xs text-gray-500">Cloud provider</div>
                            <div className="text-sm font-medium">ISO 27001 certified</div>
                            <div className="text-sm font-medium">GDPR-compliant</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default Index;
